import type { ComponentType, ReactNode } from 'react';

/**
 * The shared list item every document listing renders (specs/changes/
 * docs-pillar-parity R5): a leading icon, the item's name over its GitHub
 * location, then an optional date and status badge on the right. The whole row
 * opens the item (full editor for specs, book reader for repos); it deliberately
 * carries NO actions — publishing happens inside the editor the row opens, next
 * to the content it acts on.
 */
export function DocItemRow({ icon: Icon, title, location, date, dateLabel, status, onOpen, openTitle }: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  /** GitHub location (owner/repo) — or the "No repo" fact for unlinked specs. */
  location: string;
  /** ISO timestamp; omitted when the item has no meaningful date yet. */
  date?: string;
  /** What the date means here ("Last updated" / "Published") — the sections'
   *  dates carry different semantics, so the hover title says which. */
  dateLabel?: string;
  /** Status badge slot (PublishedBadge / LifecycleBadge); omitted for rows
   *  with no status, e.g. Components repos after the portal retired. */
  status?: ReactNode;
  onOpen: () => void;
  openTitle?: string;
}) {
  return (
    <button
      onClick={onOpen}
      title={openTitle}
      className="w-full cursor-pointer text-left flex items-center justify-between gap-4 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{title}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{location}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {date && (
          <span
            className="text-xs text-slate-400"
            title={dateLabel ? `${dateLabel} ${new Date(date).toLocaleDateString()}` : undefined}
          >
            {new Date(date).toLocaleDateString()}
          </span>
        )}
        {status}
      </div>
    </button>
  );
}
